// src/features/assessment/data/results.builder.ts

import { AssessmentResult } from '../engine/types';

export const BUILDER_RESULTS: Record<string, AssessmentResult> = {

  // ==========================================
  // RESULT 1: THE AI-NATIVE FOUNDER (New App)
  // ==========================================
  ai_native_founder: {
    id: 'ai_native_founder',
    track: 'builder',
    title: "The AI-Native Founder",
    verdict: "Building a Next-Gen Platform",
    hero_image: "/images/illustrations/results/native_hero.avif",
    
    diagnosis: {
      headline: "You are building a Thinking Engine.",
      text: "You are not building a 'Wrapper' (a thin UI over ChatGPT). You are building a system where the AI is the Product. Your app needs to think, plan, browse the web, or write code. This requires a completely different architecture than a standard SaaS."
    },
    
    solution: {
      headline: "Do not build the plumbing.",
      text: "Building a true Agentic Platform from scratch (Vector DBs, RAG pipelines, Orchestration) is the biggest trap. We provide **Canvas OS**—a complete 'Game Engine' for AI apps. You skip the 12 months of infrastructure work and focus on your unique logic."
    },

    dynamic_content: {
      default: {
        title: "1-Month MVP Roadmap",
        objective: "Launch a defensible, complex AI product in 30 days.",
        roadmap_title: "Rapid Asset Assembly",
        steps: [
          { phase: "Week 1", name: "The Foundation", detail: "Provision GPU Nodes, Vector DB, and Auth layer via Canvas OS." },
          { phase: "Week 2", name: "The Logic Kernel", detail: "Use the Visual Agents Builder to construct your unique 'Agentic Loop'." },
          { phase: "Week 3", name: "Generative UI", detail: "Build dynamic frontend components so the AI can render charts/forms." },
          { phase: "Week 4", name: "Launch", detail: "Run automated Evals and push to production." }
        ],
        read_more: { title: "Read: The Brain in a Jar", slug: "brain-in-a-jar" }
      },
      // Fallbacks
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The Brain in a Jar", subtitle: "Connecting AI to tools.", slug: "brain-in-a-jar" },
        { title: "Beyond the Sparkle Button", subtitle: "Generative UI patterns.", slug: "beyond-sparkle-button-ai-features" }
      ]
    },

    cta: {
      primary: { label: "Start Building POC", variant: "start_poc", cal_params: { note: "Outcome: AI Native Founder" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 2: THE IN-APP CONCIERGE (Existing App / Action)
  // ==========================================
  in_app_concierge: {
    id: 'in_app_concierge',
    track: 'builder',
    title: "The In-App Concierge",
    verdict: "Active Copilot Architecture",
    hero_image: "/images/illustrations/results/concierge_hero.avif",
    
    diagnosis: {
      headline: "You want 'Hands', not just a Voice.",
      text: "You are building an Agent that *does work* inside your app (e.g., 'Upgrade my plan', 'Cancel order'), not just a chatbot. Usually, rewriting your backend to support this is expensive and risky."
    },
    
    solution: {
      headline: "The Frontend Shortcut",
      text: "We don't need to rewrite your backend. We inject an Intelligence Layer directly into your Frontend. The Agent 'reads' the screen (DOM) and triggers existing hooks (`onUpgradeClick()`), saving months of refactoring."
    },

    dynamic_content: {
      default: {
        title: "Integration Roadmap",
        objective: "Inject intelligence without breaking legacy code.",
        roadmap_title: "The Sidecar Injection",
        steps: [
          { phase: "Phase 1", name: "Map Journeys", detail: "Identify the top 5 high-friction flows (e.g., Onboarding, Reporting)." },
          { phase: "Phase 2", name: "Deploy SDK", detail: "Inject the App Assistant Node into your React/Vue/Angular app." },
          { phase: "Phase 3", name: "Generative UI", detail: "Enable the agent to render buttons and charts, not just text." }
        ],
        read_more: { title: "Read: Beyond the Sparkle Button", slug: "beyond-sparkle-button-ai-features" }
      },
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "Beyond the Sparkle Button", subtitle: "Why text interfaces are dead.", slug: "beyond-sparkle-button-ai-features" },
        { title: "The Brain in a Jar", subtitle: "How Agents use 'Hands'.", slug: "brain-in-a-jar" }
      ]
    },

    cta: {
      primary: { label: "Book Architecture Audit", variant: "book", cal_params: { note: "Outcome: In-App Concierge" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 3: FEATURE INJECTION (Existing App / Read-Only)
  // ==========================================
  feature_injection: {
    id: 'feature_injection',
    track: 'builder',
    title: "The Feature Injection",
    verdict: "Intelligence as a Feature",
    hero_image: "/images/illustrations/results/feature_hero.avif",
    
    diagnosis: {
      headline: "You want to unlock your data.",
      text: "You have valuable data, but users have to search manually to find insights. You want to add a 'Chat with your Data' feature without altering your core product logic."
    },
    
    solution: {
      headline: "The Intelligence Layer",
      text: "We attach a 'Sidecar Brain' to your existing stack. We sync your disparate data (User Logs, Transaction History) into a structured Knowledge Graph, allowing users to ask questions like 'Show me all Q3 projects over budget'."
    },

    dynamic_content: {
      default: {
        title: "RAG Roadmap",
        objective: "Low-risk, high-value intelligence.",
        roadmap_title: "The Data Pipeline",
        steps: [
          { phase: "Step 1", name: "Data Topology", detail: "Map your database schema and define permission boundaries." },
          { phase: "Step 2", name: "Ingestion", detail: "Build the pipeline that syncs User Data into the Vector Store." },
          { phase: "Step 3", name: "The Feature Drop", detail: "Deploy the Sidebar Widget or 'Smart Summary' components." }
        ],
        read_more: { title: "Read: The Stupid Agent Trap", slug: "stupid-agent-trap-knowledge-base" }
      },
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The Stupid Agent Trap", subtitle: "Why simple embeddings fail.", slug: "stupid-agent-trap-knowledge-base" },
        { title: "The Brain in a Jar", subtitle: "Connecting AI to tools.", slug: "brain-in-a-jar" }
      ]
    },

    cta: {
      primary: { label: "Book Data Audit", variant: "book", cal_params: { note: "Outcome: Feature Injection" } },
      secondary: { label: "Save Roadmap", variant: "save" }
    }
  },

  // ==========================================
  // RESULT 4: THE WRAPPER WARNING (Risk)
  // ==========================================
  wrapper_warning: {
    id: 'wrapper_warning',
    track: 'builder',
    title: "The Wrapper Warning",
    verdict: "Risk of Commoditization",
    hero_image: "/images/illustrations/results/warning_hero.avif",
    
    diagnosis: {
      headline: "You are renting your core value.",
      text: "Your product idea relies heavily on the raw capability of foundational models (like GPT-4). The Risk: If OpenAI releases an update next month with a better UI, they could kill your entire business model overnight (The 'Sherlock' Risk)."
    },
    
    solution: {
      headline: "From Wrapper to Workflow",
      text: "To survive, you must integrate deeper than the model providers can. You need to move from 'Generating Text' (commodity) to 'Owning a Workflow' (defensible business) by leveraging proprietary user context and complex tool execution."
    },

    dynamic_content: {
      default: {
        title: "The Pivot Strategy",
        objective: "Build a defensible moat.",
        roadmap_title: "The Vertical AI Roadmap",
        steps: [
          { phase: "Phase 1", name: "Context Moat", detail: "Ingest proprietary user history so the AI knows things ChatGPT doesn't." },
          { phase: "Phase 2", name: "Action Moat", detail: "Stop outputting text. Start executing API calls (Gmail, Stripe)." },
          { phase: "Phase 3", name: "Interface Moat", detail: "Build a specialized Generative UI that fits the ergonomics of the job." }
        ],
        read_more: { title: "Read: The End of SaaS", slug: "the-end-of-saas" }
      },
      engineering: { title: "", objective: "", roadmap_title: "", steps: [] },
      sales: { title: "", objective: "", roadmap_title: "", steps: [] },
      marketing: { title: "", objective: "", roadmap_title: "", steps: [] },
      ops: { title: "", objective: "", roadmap_title: "", steps: [] }
    },

    intelligence: {
      title: "Recommended Intelligence",
      articles: [
        { title: "The End of SaaS", subtitle: "Why vertical integration wins.", slug: "the-end-of-saas" },
        { title: "Beyond the Sparkle Button", subtitle: "Why text interfaces are dead.", slug: "beyond-sparkle-button-ai-features" }
      ]
    },

    cta: {
      primary: { label: "Book Strategy Call", variant: "consultation", cal_params: { note: "Outcome: Wrapper Risk Pivot" } },
      secondary: { label: "Join Newsletter", variant: "newsletter" }
    }
  }
};